import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-react'
import { useLayoutEffect, useMemo, useRef } from 'react'

import { IconButton } from '../IconButton'

function getMonths (locale = 'en-US') {
  const months: { value: number; label: string }[] = []

  const formatter = new Intl.DateTimeFormat(locale, { month: 'long' })

  for (let i = 0; i < 12; i++) {
    months.push({
      value: i,
      // We construct a `Date` object to be able to localize the month name. The
      // year doesn't matter so it's been arbitrarily set to 2000.
      label: formatter.format(new Date(2000, i)),
    })
  }

  return months
}

function formatMonth (month: number, locale = 'en-US') {
  return new Intl.DateTimeFormat(locale, { month: 'long' }).format(
    new Date(2000, month)
  )
}

interface NavSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  children: React.ReactNode;
}

function NavSelect ({ children, ...props }: NavSelectProps) {
  const displayValueRef = useRef<HTMLSpanElement>(null)
  const selectRef = useRef<HTMLSelectElement>(null)

  useLayoutEffect(() => {
    const displayValue = displayValueRef.current
    const select = selectRef.current

    if (!displayValue || !select) return

    function updateWidth () {
      if (displayValue && select) {
        const selectedOption = select.querySelector('option:checked')

        if (selectedOption) {
          displayValue.textContent = selectedOption.textContent
          select.style.width = `${displayValue.offsetWidth}px`
        }
      }
    }

    updateWidth()

    const observer = new ResizeObserver(updateWidth)
    observer.observe(displayValue)

    return () => {
      observer.disconnect()
    }
  }, [props.value])

  return (
    <span className='group/nav-select relative flex items-center'>
      <span
        aria-hidden
        className='h-0 body absolute inline-block overflow-hidden'
        ref={displayValueRef}
      />
      <select
        {...props}
        className='box-content body p-2 border border-gray-200 focus:border-thatch-500 rounded-lg bg-none bg-transparent pr-5 hover:bg-gray-50 focus:bg-thatch-50 cursor-pointer transition-colors ring-inset ring-1 ring-transparent focus:ring-thatch-500'
        ref={selectRef}
      >
        {children}
      </select>
      <span className='pointer-events-none flex items-center text-gray-500 group-has-[:open]/nav-select:rotate-180 absolute right-1 top-0 bottom-0'>
        <ChevronDownIcon aria-hidden size={16} />
      </span>
    </span>
  )
}

interface NavProps {
  locale?: string;
  maxDate?: Date;
  maxYear: number;
  minDate?: Date;
  minYear: number;
  month: number;
  year: number;
  setMonth: (month: number) => void;
  setYear: (year: number) => void;
  setFocusedDate: (date: number) => void;
}

export function Nav ({
  locale = navigator.language ?? 'en-US',
  maxDate,
  maxYear: maxYearProp,
  minDate,
  minYear: minYearProp,
  month,
  year,
  setMonth,
  setYear,
  setFocusedDate,
}: NavProps) {
  const minMonth = minDate?.getMonth() ?? 0
  const minYear = minDate?.getFullYear() ?? minYearProp
  const maxMonth = maxDate?.getMonth() ?? 11
  const maxYear = maxDate?.getFullYear() ?? maxYearProp

  const years = useMemo(() => {
    return Array.from(
      { length: maxYear - minYear + 1 },
      (_, index) => minYear + index
    )
  }, [minYear, maxYear])

  const months = useMemo(() => getMonths(locale), [locale])

  return (
    <nav className='flex justify-between items-center mb-4 gap-2'>
      <IconButton
        disabled={month === minMonth && year === minYear}
        icon={ChevronLeftIcon}
        label='Previous month'
        onClick={() => {
          if (month === 0) {
            setMonth(11)
            setYear(year - 1)
          } else {
            setMonth(month - 1)
          }

          setFocusedDate(1)
        }}
      />
      <div className='flex items-center gap-2'>
        <NavSelect
          aria-label='Month'
          value={month}
          onChange={({ target }) => {
            setMonth(Number(target.value))
          }}
        >
          {months.map(({ value, label }) => {
            const isDisabled = (year === minYear && Number(value) < minMonth) ||
              (year === maxYear && Number(value) > maxMonth)

            return (
              <option
                key={value}
                disabled={isDisabled}
                value={value}
              >
                {label}
              </option>
            )
          })}
        </NavSelect>
        <NavSelect
          aria-label='Year'
          value={year}
          onChange={({ target }) => {
            setYear(Number(target.value))
          }}
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </NavSelect>
      </div>
      <span role='status' aria-live='polite' className='sr-only'>
        {formatMonth(month, locale)} {year}
      </span>
      <IconButton
        disabled={month === maxMonth && year === maxYear}
        icon={ChevronRightIcon}
        label='Next month'
        onClick={() => {
          if (month === 11) {
            setMonth(0)
            setYear(year + 1)
          } else {
            setMonth(month + 1)
          }

          setFocusedDate(1)
        }}
      />
    </nav>
  )
}

Nav.displayName = 'Calendar.Nav'
